import React, { createContext, useContext, useState, ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import { AlertColor } from '@mui/material/Alert';
import { Box, styled } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

type SnackbarContextType = {
    showSnackbar: (message: string, severity?: AlertColor) => void;
    enqueueSnackbar: (message: string, options: { variant: AlertColor }) => void;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

const getSnackbarColor = (severity: AlertColor, theme: any) => {
    switch (severity) {
        case 'success':
            return theme.palette.successGreen.main;
        case 'error':
            return theme.palette.errorRed.main;
        case 'warning':
            return theme.palette.warningOrange.main;
        case 'info':
        default:
            return theme.palette.primary.main;
    }
};

const CustomSnackbar = styled(Snackbar, {
    shouldForwardProp: (prop) => prop !== 'severity',
})<{ severity?: AlertColor }>(({ theme, severity = 'info' }) => ({
    background: theme.palette.neutralBlack.main,
    borderTopRightRadius: '8px',
    borderTopLeftRadius: '8px',
    padding: '16px',
    borderBottom: `5px solid ${getSnackbarColor(severity, theme)}`,

    '& .snackbar-content': {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontWeight: 600,
        fontSize: '16px',
        lineHeight: '20px',
        color: theme.palette.neutralWhite.main,
    },
}));

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) throw new Error('useSnackbar must be used within a SnackbarProvider');
    return context;
};

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<AlertColor>('info');

    const showSnackbar = (msg: string, sev: AlertColor = 'info') => {
        setMessage(msg);
        setSeverity(sev);
        setOpen(true);
    };

    const enqueueSnackbar = (msg: string, sev: {
        variant: AlertColor;
    }) => {
        setMessage(msg);
        setSeverity(sev.variant);
        setOpen(true);
    }

    const handleClose = () => setOpen(false);

    const SnackbarIcon = () => {
        switch (severity) {
            case 'error':
                return <ErrorIcon sx={{ width: 20, height: 20, color: 'errorRed.main' }} />;
            case 'warning':
                return <WarningIcon sx={{ width: 20, height: 20, color: 'warningOrange.main' }} />;
            case 'success':
                return <CheckCircleIcon sx={{ width: 20, height: 20, color: 'successGreen.main' }} />;
            case 'info':
            default:
                return <InfoIcon sx={{ width: 20, height: 20, color: 'primary.main' }} />;
        }
    };

    return (
        <SnackbarContext.Provider value={{ showSnackbar, enqueueSnackbar }}>
            {children}
            <CustomSnackbar open={open} autoHideDuration={4000} onClose={handleClose} severity={severity}>
                <Box className="snackbar-content">
                    <SnackbarIcon />
                    {message}
                </Box>
            </CustomSnackbar>
        </SnackbarContext.Provider>
    );
};
